import React, { useState, useEffect, createContext, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, MapPin, Phone, Mail, Instagram, Facebook, Twitter, Youtube, Globe, Shield, Headphones, ArrowRight, Plane, Heart, Star } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import BookingModal from './BookingModal';
import { cmsAPI } from '../../../services/api';
import { normalizeImageUrl } from '../../../utils/normalizeImageUrl';
import { getApiBase } from '../../../utils/apiBase';
import { dedupedFetchJson } from '../../../utils/apiDedup';

// Site Settings Context
const SiteSettingsContext = createContext(null);

export const useSiteSettings = () => useContext(SiteSettingsContext);

const navLinks = [
  { name: 'Destinations', path: '/destinations' },
  { name: 'Packages', path: '/packages' },
  { name: 'Group Trips', path: '/group-travel' },
  { name: 'Blog', path: '/blog' },
  { name: 'About', path: '/about' },
  { name: 'Contact', path: '/contact' }
];

// Scroll to top utility
const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const FooterLink = ({ to, children }) => {
  return (
    <a 
      href={to} 
      target="_blank"
      rel="noopener noreferrer"
      className="text-slate-400 hover:text-rose-400 transition-colors"
    >
      {children}
    </a>
  );
};

// Stable static fallback — used only when no env/cache/CMS URL is available, or as load-error recovery
const STATIC_LOGO = '/funstay-logo-color.png';
const LOGO_CACHE_KEY = 'fs_header_logo_v1';

// Build-time deployment-configured production logo. Treated as the canonical first-paint source.
// When set, it eliminates the visible swap for true first-time visitors as well.
const ENV_LOGO = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_HEADER_LOGO_URL) || '';

// Resolve initial src for the first React render.
// Priority:
//   1. Build-time env logo (deployment configuration)
//   2. window.__FS_INITIAL_LOGO__ published by pre-React inline script (cache hit)
//   3. localStorage cache (defensive — script may have been blocked by CSP)
//   4. STATIC_LOGO fallback
const getInitialLogoSrc = () => {
  if (ENV_LOGO) return ENV_LOGO;
  try {
    if (typeof window !== 'undefined' && window.__FS_INITIAL_LOGO__) return window.__FS_INITIAL_LOGO__;
    const cached = typeof window !== 'undefined' ? window.localStorage?.getItem(LOGO_CACHE_KEY) : null;
    if (cached && (cached.startsWith('http') || cached.startsWith('/'))) return cached;
  } catch (_) { /* localStorage may be disabled */ }
  return STATIC_LOGO;
};

export const Header = ({ onLoginClick, onBookClick, settings }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  // Logo src is initialized SYNCHRONOUSLY from cache so the first render already shows the CMS logo for returning visitors.
  const [logoSrc, setLogoSrc] = useState(getInitialLogoSrc);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const ap = settings?._agencyProfile;
  // Once settings hydrate, derive the canonical CMS logo URL and refresh the cache.
  useEffect(() => {
    if (!settings) return;
    const cmsLogo = normalizeImageUrl(
      settings?.logos?.placements?.find(p => p.id === 'public_header' || p.id === 'main_header')?.current_logo
      || settings?.logos?.header_logo
      || settings?._agencyProfile?.logo_url
    );
    if (!cmsLogo) return;
    if (cmsLogo !== logoSrc) {
      setLogoSrc(cmsLogo);
    }
    try { window.localStorage?.setItem(LOGO_CACHE_KEY, cmsLogo); } catch (_) { /* storage disabled */ }
  }, [settings, logoSrc]);

  const handleBookNowClick = () => {
    const isGroupTripDetail = /^\/group-travel\/[^/]+$/.test(location.pathname || '');
    if (isGroupTripDetail) {
      const groupTripCta = document.querySelector('[data-testid="group-book-now-btn"]');
      if (groupTripCta instanceof HTMLElement) {
        groupTripCta.scrollIntoView({ behavior: 'smooth', block: 'center' });
        groupTripCta.click();
        return;
      }
    }
    onBookClick();
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-in-out ${
        scrolled ? 'bg-slate-900 shadow-lg' : ''
      }`}
      style={{ background: scrolled ? undefined : 'transparent' }}
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo — stable dimensions, eager-loaded; src comes from cached CMS URL when available */}
          <Link to="/" className="flex items-center">
            <img 
              src={logoSrc} 
              alt={ap?.company_name || settings?.brand_name || 'Funstay'} 
              width="180"
              height="56"
              loading="eager"
              decoding="async"
              fetchPriority="high"
              className="h-12 md:h-14 w-auto object-contain bg-transparent max-w-[180px] transition-opacity duration-200"
              onError={(e) => {
                // Image-load failure recovery: fall back to static logo and clear stale cache.
                if (e.currentTarget.src.indexOf(STATIC_LOGO) === -1) {
                  try { window.localStorage?.removeItem(LOGO_CACHE_KEY); } catch (_) { /* */ }
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = STATIC_LOGO;
                  setLogoSrc(STATIC_LOGO);
                }
              }}
              data-testid="header-logo"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            {navLinks.map((link) => (
              <a
                key={link.path}
                href={link.path}
                target="_blank"
                rel="noopener noreferrer"
                className={`text-sm font-medium transition-colors duration-200 relative ${
                  location.pathname === link.path
                    ? 'text-rose-400'
                    : 'text-white/90 hover:text-rose-400'
                }`}
              >
                {link.name}
                {location.pathname === link.path && (
                  <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-rose-400 rounded-full" />
                )}
              </a>
            ))}
          </nav>

          {/* Desktop Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <Button 
              onClick={handleBookNowClick}
              className="bg-gradient-to-r from-rose-500 to-orange-500 hover:from-rose-600 hover:to-orange-600 text-white rounded-full px-6"
            >
              Book Now
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden p-2 text-white"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="lg:hidden bg-slate-900 border-t border-slate-700">
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              <a
                key={link.path}
                href={link.path}
                target="_blank"
                rel="noopener noreferrer"
                className="block px-3 py-2 text-white/90 hover:text-rose-400 hover:bg-white/10 rounded-lg"
                onClick={() => { setIsOpen(false); }}
              >
                {link.name}
              </a>
            ))}
            <div className="pt-4 space-y-3 border-t border-slate-700 mt-4">
              <Button 
                className="w-full bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-full"
                onClick={() => { setIsOpen(false); handleBookNowClick(); }}
              >
                Book Now
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export const PartnersSection = ({ partners }) => {
  if (!partners || partners.length === 0) return null;

  // Duplicate logos for seamless marquee loop
  const marqueeLogos = [...partners, ...partners, ...partners];

  return (
    <section className="bg-slate-100 py-16 overflow-hidden" data-testid="partners-section">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <h2 className="text-4xl font-bold text-slate-800 text-center mb-12">Our Trusted Partners</h2>
      </div>
      <div className="relative">
        <div className="flex gap-16 items-center footer-partners-marquee">
          {marqueeLogos.map((p, i) => {
            const logoSrc = normalizeImageUrl(p.logo || p.image);
            return (
              <div key={i} className="flex-shrink-0 transition-transform duration-300 hover:scale-110">
                {logoSrc ? (
                  <img src={logoSrc} alt={p.alt_text || p.name || 'Partner'} className="h-20 w-auto object-contain" onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }} />
                ) : (
                  <span className="text-slate-500 font-bold text-xl tracking-wide whitespace-nowrap">{p.name}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <style>{`
        @keyframes footerPartnersMarquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
        .footer-partners-marquee {
          animation: footerPartnersMarquee 40s linear infinite;
        }
        .footer-partners-marquee:hover {
          animation-play-state: paused;
        }
      `}</style>
    </section>
  );
};

export const Footer = ({ settings }) => {
  const ap = settings?._agencyProfile;
  const footerLogo = normalizeImageUrl(
    settings?.logos?.placements?.find(p => p.id === 'public_footer' || p.id === 'footer_logo')?.current_logo 
    || settings?.logos?.footer_logo 
    || ap?.logo_url
  ) || '/funstay-logo-white.png';

  const footerSections = settings?.footer?.sections || [];
  const contactInfo = settings?.footer?.contact_info || settings?.contact || {
    address: ap?.address || '123 Travel Lane, Mumbai, India',
    phone: ap?.phone || '+91 98765 43210',
    email: ap?.email || 'hello@funstay.in'
  };
  const socialLinks = settings?.footer?.social_links || settings?.social_links || 
    (ap?.social_links ? Object.entries(ap.social_links).filter(([,v]) => v).map(([platform, url]) => ({ platform, url })) : []);
  const legalLinks = settings?.footer?.legal_links || [];
  const copyright = settings?.footer?.copyright?.replace('{year}', new Date().getFullYear()) 
    || `\u00A9 ${new Date().getFullYear()} ${settings?.brand_name || 'Funstay'}. All rights reserved.`;

  const trustStrip = settings?.footer?.trust_strip;

  const iconMap = {
    shield: Shield,
    globe: Globe,
    headphones: Headphones,
    heart: Heart,
    plane: Plane,
    star: Star,
    map: MapPin,
  };

  return (
    <footer className="bg-slate-900" data-testid="site-footer">
      {/* CTA Strip — CMS-driven */}
      {trustStrip?.enabled !== false && (
        <div className="bg-gradient-to-r from-rose-600 via-orange-500 to-amber-500">
          <div className="max-w-7xl mx-auto px-4 lg:px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0">
                <Plane className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white" data-testid="trust-strip-heading">
                  {trustStrip?.heading || 'Plan your dream vacation with experts'}
                </h3>
                <p className="text-white/80 text-sm mt-0.5" data-testid="trust-strip-subheading">
                  {trustStrip?.subheading || 'Customized itineraries, best prices, 24/7 support'}
                </p>
              </div>
            </div>
            <a href={trustStrip?.cta_link || '/inquiry'} target="_blank" rel="noopener noreferrer">
              <Button className="bg-white text-rose-600 hover:bg-white/90 rounded-full px-8 py-3 font-semibold shadow-lg shadow-black/10 flex items-center gap-2" data-testid="trust-strip-cta">
                {trustStrip?.cta_text || 'Start Planning'} <ArrowRight className="w-4 h-4" />
              </Button>
            </a>
          </div>
        </div>
      )}

      {/* Trust Badges — CMS-driven */}
      {trustStrip?.items?.length > 0 && (
        <div className="border-b border-slate-800">
          <div className="max-w-7xl mx-auto px-4 lg:px-8 py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {trustStrip.items.filter(item => item.enabled !== false).map((item, idx) => {
                const IconComp = iconMap[item.icon] || Shield;
                const colorClasses = ['text-emerald-400', 'text-sky-400', 'text-amber-400', 'text-rose-400'];
                return (
                  <div key={item.id || idx} className="flex items-center gap-3" data-testid={`trust-badge-${item.id || idx}`}>
                    <div className="w-10 h-10 bg-slate-800 rounded-xl flex items-center justify-center flex-shrink-0">
                      <IconComp className={`w-5 h-5 ${colorClasses[idx % colorClasses.length]}`} />
                    </div>
                    <div>
                      <p className="text-white text-sm font-semibold">{item.title}</p>
                      <p className="text-slate-500 text-xs">{item.subtitle}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Main Footer Grid */}
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Column 1: Brand */}
          <div className="lg:col-span-1">
            <Link to="/" onClick={scrollToTop} className="inline-block">
              <img src={footerLogo} alt={ap?.company_name || settings?.brand_name || 'Funstay'} loading="lazy" decoding="async" className="h-12 md:h-14 w-auto object-contain bg-transparent max-w-[180px]" onError={(e) => { e.target.onerror = null; e.target.src = '/funstay-logo-white.png'; }} data-testid="footer-logo" />
            </Link>
            <p className="mt-4 text-sm text-slate-400 leading-relaxed">
              {settings?.tagline || 'Creating unforgettable travel experiences across the globe.'}
            </p>
            <div className="flex space-x-3 mt-6">
              {socialLinks.filter(s => s.enabled).map((social) => {
                const Icon = social.platform === 'instagram' ? Instagram :
                            social.platform === 'facebook' ? Facebook :
                            social.platform === 'twitter' ? Twitter :
                            social.platform === 'youtube' ? Youtube : null;
                return Icon ? (
                  <a key={social.platform} href={social.url || '#'} target="_blank" rel="noopener noreferrer"
                    className="w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-rose-500 transition-all duration-300 group">
                    <Icon className="w-4 h-4 text-slate-400 group-hover:text-white transition-colors" />
                  </a>
                ) : null;
              })}
            </div>
          </div>

          {/* Row 1 CMS Sections (first 3) */}
          {footerSections.slice(0, 3).map((section) => (
            <div key={section.id}>
              <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-5">{section.title}</h4>
              <ul className="space-y-3">
                {(section.links || []).filter(link => link.enabled).map((link) => (
                  <li key={link.id}>
                    <FooterLink to={link.url}>{link.label}</FooterLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact Column — Row 2, Col 1 */}
          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-5">Get In Touch</h4>
            <ul className="space-y-4">
              <li>
                <a href={`tel:${contactInfo.phone}`} className="flex items-start gap-3 text-slate-400 hover:text-rose-400 transition-colors group">
                  <div className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-rose-500/10 transition-colors">
                    <Phone className="w-4 h-4 text-rose-400" />
                  </div>
                  <span className="text-sm pt-1">{contactInfo.phone}</span>
                </a>
              </li>
              <li>
                <a href={`mailto:${contactInfo.email}`} className="flex items-start gap-3 text-slate-400 hover:text-rose-400 transition-colors group">
                  <div className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-rose-500/10 transition-colors">
                    <Mail className="w-4 h-4 text-rose-400" />
                  </div>
                  <span className="text-sm pt-1">{contactInfo.email}</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-slate-400">
                  <div className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-rose-400" />
                  </div>
                  <span className="text-sm pt-1">{contactInfo.address}</span>
                </div>
              </li>
            </ul>
          </div>

          {/* Row 2 CMS Sections (remaining) */}
          {footerSections.slice(3).map((section) => (
            <div key={section.id}>
              <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-5">{section.title}</h4>
              <ul className="space-y-3">
                {(section.links || []).filter(link => link.enabled).map((link) => (
                  <li key={link.id}>
                    <FooterLink to={link.url}>{link.label}</FooterLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-5">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">{copyright}</p>
            <div className="flex items-center gap-6">
              {legalLinks.filter(link => link.enabled).map((link) => (
                <a key={link.id} href={link.url} target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-rose-400 text-sm transition-colors">
                  {link.label}
                </a>
              ))}
              <span className="text-slate-600 text-sm">Made with <Heart className="w-3 h-3 text-rose-500 inline-block" /> for travelers</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children, showPartners = false }) => {
  const [showBooking, setShowBooking] = useState(false);
  const [siteSettings, setSiteSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Both reads are deduped at the network layer (apiDedup, 60s TTL).
        // Any other component asking for `/api/cms/settings` or
        // `/api/settings/agency-profile` within the same page-load shares
        // the same single round-trip.
        const [settings, agencyProfile] = await Promise.all([
          cmsAPI.getSettings(),
          dedupedFetchJson(`${getApiBase()}/api/settings/agency-profile`),
        ]);
        if (agencyProfile) settings._agencyProfile = agencyProfile;
        setSiteSettings(settings);
      } catch (error) {
        console.error('Error fetching site settings:', error);
      }
    };
    fetchSettings();
  }, []);

  // Derive partners from footer settings (global source)
  const partners = siteSettings?.footer?.partners?.logos || [];

  return (
    <SiteSettingsContext.Provider value={siteSettings}>
      <div className="min-h-screen bg-slate-50">
        <Header 
          onBookClick={() => setShowBooking(true)}
          settings={siteSettings}
        />
        
        <main>
          {children}
        </main>
        
        <PartnersSection partners={partners} />
        <Footer settings={siteSettings} />
        
        <BookingModal isOpen={showBooking} onClose={() => setShowBooking(false)} />
      </div>
    </SiteSettingsContext.Provider>
  );
};

export default Layout;
